<script lang="ts">
  import type { Snippet } from 'svelte'
  import { faXmark } from '@fortawesome/free-solid-svg-icons'
  import Icon from './Icon.svelte'

  let {
    open = $bindable(false),
    closeLabel,
    children,
  }: {
    open?: boolean
    closeLabel: string
    children: Snippet<[{ close: () => void }]>
  } = $props()

  let dialog: HTMLDialogElement

  $effect(() => {
    if (open) {
      dialog.showModal()
    }
  })

  function close() {
    dialog.close()
  }

  function handleDialogClick(event: MouseEvent) {
    const rect = dialog.getBoundingClientRect()
    const clickedInside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom

    if (!clickedInside) {
      close()
    }
  }
</script>

<dialog
  bind:this={dialog}
  class="drawer"
  closedby="any"
  onclick={handleDialogClick}
  onclose={() => (open = false)}
>
  <button class="drawer-close" aria-label={closeLabel} onclick={close}>
    <Icon icon={faXmark} />
  </button>
  {@render children({ close })}
</dialog>

<style>
  .drawer {
    position: fixed;
    inset-block: 0;
    inset-inline: auto 0;
    margin: 0;
    border: none;
    width: 80%;
    max-width: none;
    height: 100%;
    max-height: none;
    display: none;
    flex-direction: column;
    padding: calc(var(--nav-height) + var(--space-4)) var(--space-4) var(--space-4);
    overflow-y: auto;
    background-color: var(--color-white);
    box-shadow: calc(-1 * var(--direction) * var(--space-4)) 0 var(--space-10) var(--shadow-1);
    transform: translateX(calc(100% * var(--direction)));
    transition:
      transform 0.25s ease-out,
      overlay 0.25s ease-out allow-discrete,
      display 0.25s ease-out allow-discrete;

    &[open] {
      display: flex;
      transform: translateX(0);
    }

    @starting-style {
      &[open] {
        transform: translateX(calc(100% * var(--direction)));
      }
    }

    @media (--tablet) {
      display: none;
    }

    &::backdrop {
      background-color: var(--shadow-2);
      opacity: 0;
      transition:
        opacity 0.25s ease-out,
        overlay 0.25s ease-out allow-discrete,
        display 0.25s ease-out allow-discrete;
    }

    &[open]::backdrop {
      opacity: 1;
    }

    @starting-style {
      &[open]::backdrop {
        opacity: 0;
      }
    }
  }

  .drawer-close {
    position: absolute;
    inset-block-start: var(--space-4);
    inset-inline-end: var(--space-4);
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-text);
    padding: var(--space-2);
  }
</style>
